import React, { useContext, useEffect, useMemo, useState } from "react";
import { Content, Wrapper, UploadContainer } from "./style";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getMerchantDetails } from "../../../pages/Settings/settingsSlice";
import {
  getRequiredDocumentsByType,
  getUploadedDocumentsByBusinessId,
  uploadRequiredDocument,
} from "../../../pages/Compliance/complianceSlice";
import Loader from "../../common/Loader";
import UploadHeader from "../../compliance/Steps/components/UploadInfo";
import SelfCustomUpload from "../../common/CustomUpload/SelfCustomUpload";
import { stringToSentenceCase } from "../../../utils/format";
import { SuccessAlertContext } from "../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../context/NetworkErrorAlert";
import { getCountryValue, UploadFn } from "../../compliance/utils";
import { addDirectorDocuments, getListOfDirectors } from "../../../pages/Onboarding/redux/actions";
import { DirectorDocumentField } from "../../../pages/Onboarding/Types";
import { updateDirectorsList } from "../../../pages/Onboarding/redux/slice";
import { getDirectorId, getMostRecentUploadedDocs } from "../../../pages/Onboarding/utils/businessDocumentUtils";
import { listOfDirectorsDocuments } from "../../../pages/Onboarding/utils/constants";
import { useRequiredDocs } from "../../../app/hooks/useRequiredDocs";
import { getMerchantBusinessDetails } from "../../../utils/fetchMerchantBusDetails";
import { updateBusinessObj } from "../../../pages/Settings/settingsSlice";

const Verification = () => {
  const dispatch = useAppDispatch();
  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext,
  );
  const { onShowAlert } = useContext(SuccessAlertContext);
  const [uploadDetails, setUploadDetails] = useState([]);

  const { status: businessStatus, businessId, userId, userObj } = useAppSelector(
    (state) => state.users || {},
  );
  const businessCountry = userObj?.accountUser?.businessCountry;
  const { business } = useAppSelector((state) => state.settings);
  const { directorsList, loading } = useAppSelector((state) => state.onboarding)

  const businessTypeId = business?.businessType
  const { requiredDocs } = useRequiredDocs({ businessTypeId, businessCountry })

  const {
    status: docStatus,
    documents,
    uploadedDocuments,
  } = useAppSelector((state) => state.compliance);

  useEffect(() => {
    if (!businessId) {
      getMerchantBusinessDetails(dispatch, userId, updateBusinessObj)
    }
  }, [business, userId])

  useEffect(() => {
    if (businessId) {
      if (Object.keys(business).length < 1) {
        dispatch(getMerchantDetails(userId))
      }
      dispatch(getUploadedDocumentsByBusinessId({ businessId }));
    }

    const fetchListOfDirectors = async () => {
      const directorList = await dispatch(getListOfDirectors(businessId))
      
      if (getListOfDirectors.fulfilled.match(directorList)) {
        dispatch(updateDirectorsList(directorList.payload))
      }
    }
    fetchListOfDirectors()
  }, [businessId, userId]);

  useEffect(() => {
    if (business && Object.keys(business).length) {
      dispatch(
        getRequiredDocumentsByType({
          businessType: business.businessType,
          country: getCountryValue(business.country),
        }),
      );
    }
  }, [business, dispatch]);

  const isBusinessDocument = (directorName, approvalStatus) =>
    directorName === null || approvalStatus === "NOT UPLOADED";

  const uploadDocument = (busType): UploadFn => (file, onUploadProgress) => {
    const { businessDocument, directorName, documentApprovalStatus } = busType;

    if (isBusinessDocument(directorName, documentApprovalStatus)) {
      return dispatch(
        uploadRequiredDocument({
          businessId,
          businessDocumentId: businessDocument.id,
          file,
          onUploadProgress,
        }),
      ).unwrap();
    }

    const directorDocsMap: Record<string, DirectorDocumentField> = {
      photographId: "photograph",
      identityId: "idDocument",
      proofOfAddressId: "proofOfAddress",
    }

    const document = listOfDirectorsDocuments.find(doc => 
      doc.photographId === businessDocument.document || 
      doc.identityId === businessDocument.document || 
      doc.proofOfAddressId === businessDocument.document
    )

    const fileField = document && Object.entries(directorDocsMap).find(([idField]) => 
      document[idField] === businessDocument.document
    )?.[1]

    if (!fileField) {
      return Promise.reject(new Error("This director document type is not supported."));
    }

    return dispatch(
      addDirectorDocuments([{
        director: {
          businessId,
          name: directorName,
          id: getDirectorId(directorName, directorsList),
        },
        files: { [fileField]: file },
      }]),
    ).unwrap();
  };

  const onDocumentUploaded = (busType) => () => {
    dispatch(getUploadedDocumentsByBusinessId({ businessId }))
    onShowAlert(
      isBusinessDocument(busType?.directorName, busType?.documentApprovalStatus)
        ? "The business document has been updated successfully"
        : "The director document has been updated successfully"
    );
  };

  const onUploadDocumentDone = async (newDoc: any) => {
    const newDetails = [...uploadDetails];
    const detailsIndex = uploadDetails.findIndex((det) => det.id === newDoc.id);
    if (detailsIndex !== -1) {
      newDetails.splice(detailsIndex, 1, newDoc);
      setUploadDetails(newDetails);
    }
  };

  const mostRecentUploadedDocs = useMemo(() => {
    return getMostRecentUploadedDocs(uploadedDocuments, requiredDocs)
  }, [uploadedDocuments, requiredDocs])

  return (
    <Wrapper>
      <Loader isLoading={businessStatus === "loading" || docStatus === "loading" || loading} />

      <Content>
        {mostRecentUploadedDocs?.map((busType) => {
          return ["document", "upload", "website", "proof_of_address"].includes(
            busType?.businessDocument?.type,
          ) ? (
            <UploadContainer key={busType.businessDocument.id+busType.directorName}>
              <UploadHeader
                label={busType.businessDocument.description}
                title={
                  busType.businessDocument.document
                    ? stringToSentenceCase(busType.businessDocument.document + (busType?.directorName ? ` (${busType?.directorName})` : ''))
                    : ""
                }
              />
              <SelfCustomUpload
                onUpload={onUploadDocumentDone}
                docId={busType.businessDocument.id}
                docName={busType.businessDocument.document}
                approvalStatus={busType?.documentApprovalStatus}
                upload={uploadDocument(busType)}
                onUploadFinish={onDocumentUploaded(busType)}
              />
            </UploadContainer>
          ) : null;
        })}
      </Content>
    </Wrapper>
  );
};

export default Verification;
